import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Layout from "./Layout";
import { profileConfirmed, useAuth } from "../auth/AuthContext";
import NotificationBell from "./NotificationBell";

// Layout (byte-identical to boku-ui) with this app's navigation and user menu.
// Every signed-in page renders inside <AppLayout title=... crumbs=[...]>.
//
//   crumbs: [{ label, to? }, ...] — rendered above the title as a breadcrumb trail.
//
// On a phone (below 768 px) Layout puts <Nav/> and <UserMenu/> behind its menu button; the
// stylesheet (Shared.jsx globalStyles) lays them out there as 44 px rows, so nothing here
// changes for it (knowledge/reference/mobile-layout.md).
//
// The page title also becomes the document title ("Water samples 2026 · Unio"), so each page
// has its own <title> (WCAG 2.4.2) and a screen reader announces where a link led.

export const APP_TITLE = "Unio";

export function usePageTitle(title) {
  useEffect(() => {
    document.title = title ? `${title} · ${APP_TITLE}` : `${APP_TITLE} · BOKU Core Facility`;
  }, [title]);
}

const barButton = {
  background: "rgba(0,0,0,0.18)", border: "none", color: "#fff", fontSize: 13, fontWeight: 500,
  padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
};

// "Account" (/account: the user's own profile and ORCID iD) is offered to everyone signed in.
// "Management" is offered to the admin role only; the routes behind it answer 403 to anyone
// else anyway, so hiding it is about not showing a door that does not open.
// An account whose profile is not confirmed (N5) reaches only its own profile: no navigation and
// no notification bell (the server would answer 403 to its unread count), only Sign out.
function Nav() {
  const location = useLocation();
  const { user } = useAuth();
  const link = (path, label) => {
    const active = location.pathname.startsWith(path);
    return (
      <Link
        to={path}
        aria-current={active ? "page" : undefined}
        style={{
          color: "#ffffff", textDecoration: "none", fontSize: 14, fontWeight: active ? 700 : 500,
          padding: "5px 13px", borderRadius: 6, background: active ? "rgba(0,0,0,0.22)" : "transparent",
          transition: "all 0.15s",
        }}
      >
        {label}
      </Link>
    );
  };
  if (user && !profileConfirmed(user)) return null;
  return (
    <nav aria-label="Main" style={{ display: "flex", gap: 4 }}>
      {link("/projects", "Projects")}
      {user?.is_admin && link("/admin", "Management")}
      {user && link("/account", "Account")}
    </nav>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  // A Keycloak session leaves for Keycloak's sign-out page; a password session just comes back
  // to the sign-in page.
  const signOut = async () => {
    if (!(await logout())) navigate("/login", { replace: true });
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, borderLeft: "1px solid rgba(255,255,255,0.35)", paddingLeft: 12 }}>
      {profileConfirmed(user) && <NotificationBell />}
      <span
        data-user-label
        title={user.email || undefined}
        style={{ color: "#ffffff", fontSize: 13 }}
      >
        {user.full_name || user.username}
      </span>
      <button type="button" onClick={signOut} style={barButton}>Sign out</button>
    </div>
  );
}

export function Breadcrumbs({ crumbs }) {
  if (!crumbs || crumbs.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" style={{ marginBottom: 6 }}>
      <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: 6, fontSize: 13, color: "#4b5563" }}>
        {crumbs.map((c, i) => (
          // data-break: a project or study name with no space (Steiner_wheat_…) breaks
          // anywhere instead of widening a phone's page.
          <li key={i} data-break style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            {i > 0 && <span aria-hidden="true">›</span>}
            {c.to ? <Link to={c.to} style={{ color: "var(--dp-accent-dark)" }}>{c.label}</Link>
                  : <span aria-current="page">{c.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default function AppLayout({ title, crumbs, children }) {
  usePageTitle(title);
  return (
    <Layout title={title} nav={<Nav />} right={<UserMenu />} home="/projects">
      {crumbs && <div style={{ margin: "-8px 0 18px" }}><Breadcrumbs crumbs={crumbs} /></div>}
      {children}
    </Layout>
  );
}
